import React, { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/src/renderer/components/ui/Button'
import { CalmIllustration } from '../../../components/CalmIllustration'
import { EmptyState } from '../../../components/EmptyState'
import { PageHelpButton } from '@/src/renderer/components/common/PageHelpButton'
import { useStableRandomIndex } from '../../../hooks/useStableRandomIndex'
import './QuickNotesEmptyState.scss'

/** So viele Varianten stehen unter `quickNotes.emptyTitles` in den Sprachdateien. */
const EMPTY_TITLE_COUNT = 8

export interface QuickNotesEmptyStateProps {
  /** Startet das Anlegen (wie `A`) — erst nach dem Abgang der Box. */
  onCreateTask: () => void
  /** Nur bei leerer App, nur am Desktop: Öffnen-Dialog, dann „Backup wiederherstellen“ (#158). */
  onRestoreBackup?: () => void
  /** Nur bei leerer App: die Einstellungen auf „Import & Export“ (#158). */
  onImportFromOtherApp?: () => void
  /**
   * Das **Angebot der Lernaufgaben** (#323), solange es offen ist. Es steht an der Stelle der Buttons — nicht
   * zusätzlich darüber: Auf den ersten Blick soll nur eine Frage dastehen (Sven, 13.10.2026). Nach der Antwort
   * kommen die Buttons zurück (oder die Liste füllt sich).
   */
  offer?: ReactNode
}

/**
 * Der leere Zustand in **Kurz notiert** (25.09.2026) — gebaut wie das leere
 * Projekt (`CanvasEmptyStateTeaser`): zufälliges Motiv, einer von acht Titeln,
 * gestaffelte Einblendung, Primär-Knopf mit `A`. Ohne „Gedanken sammeln": Hier
 * gibt es keinen Canvas. Der Knopf trägt den Anker für den Schein am unteren
 * Rand (`empty-state-glow-anchor`).
 *
 * Ist HappyTask ganz leer, stehen darunter zwei Wege hinein (#158, Entscheidung 15): „Aus anderer App
 * importieren …“ und „Ich habe ein Backup …“ — als Outline-Buttons nebeneinander, in dieser Reihenfolge (#322).
 *
 * @example
 * <QuickNotesEmptyState onCreateTask={() => startCreating('top', 0)} {...useEmptyAppActions()} />
 */
export const QuickNotesEmptyState: React.FC<QuickNotesEmptyStateProps> = ({ onCreateTask, onRestoreBackup, onImportFromOtherApp, offer }) => {
  const { t } = useTranslation()
  const emptyTitleIndex = useStableRandomIndex(EMPTY_TITLE_COUNT)

  return (
    <EmptyState
      className="quick-notes-empty-state"
      illustration={<CalmIllustration set="quickNotes" />}
      title={t(`quickNotes.emptyTitles.${emptyTitleIndex}`)}
      text={t('quickNotes.emptyText')}
      titleAside={<PageHelpButton page="quickNotes" />}
      actionsLayout="stack"
      // Nach „Nein danke“ kommen die Buttons herein wie beim Öffnen der Seite (Sven, 13.10.2026).
      actionsKey={offer ? 'offer' : 'buttons'}
      actions={(leave) => offer ?? (
        <>
          <Button
            variant="primary"
            className="empty-state-glow-anchor"
            shortcut={{ kind: 'character', text: 'A' }}
            onClick={() => leave(onCreateTask)}
          >
            {t('quickNotes.emptyCreate')}
          </Button>
          {(onRestoreBackup || onImportFromOtherApp) && (
            <div className="quick-notes-empty-state__more">
              {onImportFromOtherApp && (
                <Button variant="outline" onClick={onImportFromOtherApp}>{t('quickNotes.emptyImportOtherApp')}</Button>
              )}
              {onRestoreBackup && (
                <Button variant="outline" onClick={onRestoreBackup}>{t('quickNotes.emptyHasBackup')}</Button>
              )}
            </div>
          )}
        </>
      )}
    />
  )
}
